import { useState, useEffect } from 'react';
import { Trophy, RefreshCw, MapPin, Globe, ChevronRight } from 'lucide-react';

export default function TennisWidget() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('atp');
  const [error, setError] = useState(null);

  const fetchTennisResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sports/tennis`
      );
      
      if (!response.ok) throw new Error('Tennis fetch failed');
      
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (err) {
      console.error('Tennis error:', err);
      setArticles([]);
      setError('Flux tennis indisponible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTennisResults();
    // Refresh every 10 minutes
    const interval = setInterval(fetchTennisResults, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const filteredArticles = articles.filter(article =>
    activeTab === 'atp' ? article.type !== 'wta' : article.type === 'wta'
  );

  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid="tennis-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          TENNIS NEWS
        </h3>
        <button 
          onClick={fetchTennisResults}
          className="p-1 hover:bg-muted rounded transition-colors"
          title="Aktualisieren"
          data-testid="tennis-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg mb-3">
        <button
          onClick={() => setActiveTab('atp')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'atp' 
              ? 'bg-background shadow text-foreground' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tennis-tab-atp"
        >
          <Globe className="h-3.5 w-3.5" />
          ATP
        </button>
        <button
          onClick={() => setActiveTab('wta')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'wta' 
              ? 'bg-background shadow text-foreground' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tennis-tab-wta"
        >
          <MapPin className="h-3.5 w-3.5" />
          WTA
        </button>
      </div>

      {loading && articles.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="tennis-error">
          {error}
        </p>
      ) : (
        <div className="space-y-3">
          {filteredArticles.slice(0, 4).map((article, i) => (
            <a
              key={article.url || i}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-muted/30 rounded-lg p-3 hover:bg-muted/50 transition-colors"
              data-testid={`tennis-article-link-${i}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-primary" data-testid={`tennis-article-source-${i}`}>{article.source}</span>
                <span className="text-xs text-muted-foreground" data-testid={`tennis-article-date-${i}`}>{article.date}</span>
              </div>
              <div className="text-sm font-medium" data-testid={`tennis-article-title-${i}`}>{article.title}</div>
              {article.summary && (
                <p className="text-xs text-muted-foreground mt-1" data-testid={`tennis-article-summary-${i}`}>{article.summary}</p>
              )}
            </a>
          ))}
        </div>
      )}

      {filteredArticles.length === 0 && !loading && !error && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Keine aktuellen Tennis-News verfügbar
        </p>
      )}

      <div className="mt-3 pt-3 border-t border-border">
        <a 
          href="https://www.flashscore.com/tennis/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
          data-testid="tennis-more-link"
        >
          Mehr Tennis News
          <ChevronRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
